import { useMemo } from 'react';
import type { IssuesQuery } from 'shared/domain/issue/issueQuery';
import type { PaginationData } from 'shared/filter/types';
import { useProjectTimezone } from 'store/project';
import { buildIssuesQuery } from '../models/issuesQuery';
import { useIssueStore } from '../store';

/**
 * The issue grid's query (search, filters, sort, archived, optionally a page).
 * It keeps its identity while its content is the same, so effects depending
 * on it read again only after a real change; `undefined` until the project's
 * timezone is known.
 */
export function useIssuesQuery(pagination?: PaginationData): IssuesQuery | undefined {
  const parameters = useIssueStore.use.parameters();
  const filterModel = useIssueStore.use.filterModel();
  const timezone = useProjectTimezone();
  const page = pagination?.page;
  const size = pagination?.size;

  const query = useMemo(
    () =>
      timezone
        ? buildIssuesQuery({
            parameters,
            filterModel,
            timezone,
            pagination: page === undefined || size === undefined ? undefined : { page, size },
          })
        : undefined,
    [parameters, filterModel, timezone, page, size]
  );
  const queryKey = JSON.stringify(query);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `queryKey` stands for `query`.
  return useMemo(() => query, [queryKey]);
}
